import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SiteRenderer } from "../site/SiteRenderer";
import { useSiteLayout } from "../site/useSiteLayout";
import NotFound from "./NotFound";
import { PageLoader } from "../components/LoadingRing";
import { offPreviewQuery, offPreviewFetchInit } from "../lib/offPreview";
import { OffPreviewBanner } from "../components/OffPreviewBanner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function SitePage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const preview = useSiteLayout(slug);
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ["site-page-public", slug],
        queryFn: async () => {
            const res = await fetch(`${BASE}/api/site/layout/${slug}${offPreviewQuery()}`,
                offPreviewFetchInit());
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || "not found");
            }
            return res.json();
        },
        retry: false,
        enabled: !preview.editing,
    });

    // A page whose Live switch is off sends visitors to the homepage.
    useEffect(() => {
        if (error?.message === "page_off") navigate("/", { replace: true });
    }, [error, navigate]);

    useEffect(() => {
        if (!data) return;
        const seo = data.seo || {};
        document.title = seo.seo_title || data.title || "ReHealthMe";
        const meta = document.querySelector('meta[name="description"]');
        if (meta && (seo.meta_description || data.description)) {
            meta.setAttribute("content", seo.meta_description || data.description);
        }
        const robots = document.querySelector('meta[name="robots"]') || (() => {
            const m = document.createElement("meta");
            m.name = "robots";
            document.head.appendChild(m);
            return m;
        })();
        robots.setAttribute("content", seo.noindex ? "noindex, nofollow" : "index, follow");

        const upsertLink = (rel, href) => {
            let el = document.querySelector(`link[rel="${rel}"]`);
            if (!href) { el?.remove(); return; }
            if (!el) { el = document.createElement("link"); el.rel = rel; document.head.appendChild(el); }
            el.setAttribute("href", href);
        };
        const upsertOg = (property, content) => {
            let el = document.querySelector(`meta[property="${property}"]`);
            if (!content) { el?.remove(); return; }
            if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
            el.setAttribute("content", content);
        };
        upsertLink("canonical", seo.canonical_url || `${window.location.origin}/${slug}`);
        upsertOg("og:title", seo.seo_title || data.title || "");
        upsertOg("og:description", seo.meta_description || data.description || "");
        upsertOg("og:image", seo.social_image || "");
    }, [data, slug]);

    if (preview.editing) {
        return (
            <div className="pt-24 md:pt-28" data-testid={`site-page-${slug}`}>
                <SiteRenderer sections={preview.sections || []} editing selectedId={preview.selectedId}
                    onSelect={preview.onSelect} onEditText={preview.onEditText} />
            </div>
        );
    }
    if (isLoading) return (
        <div className="bg-bg0 min-h-screen flex items-center justify-center" data-testid="site-page-loading">
            <PageLoader label="LOADING PAGE…" className="" />
        </div>
    );
    if (isError || !data) return <NotFound />;

    return (
        <div className="pt-24 md:pt-28" data-testid={`site-page-${slug}`}>
            {data.live === false && <OffPreviewBanner label={data.title || `/${slug}`} />}
            <SiteRenderer sections={data.sections || []} />
            {(data.sections || []).length === 0 && (
                <div className="mx-auto max-w-3xl px-6 py-16 text-center">
                    <h1 className="font-display text-4xl uppercase tracking-tight">{data.title}</h1>
                    <p className="mt-4 text-zinc-400">This page has no content yet.</p>
                </div>
            )}
        </div>
    );
}
